import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { firstValueFrom } from 'rxjs';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ContadorQuantidadeComponent } from '@shared/components/contador-quantidade/contador-quantidade.component';
import { AcaoEtapaEnum } from '@shared/enums/compras/acao-etapa.enum';
import { StatusItemConferenciaEnum } from '@shared/enums/compras/status-item-conferencia.enum';
import { TipoDivergenciaCompraEnum } from '@shared/enums/compras/tipo-divergencia-compra.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AutomacaoService } from '@shared/services/apis/compras/automacao.service';
import { ConferenciaService } from '@shared/services/apis/compras/conferencia.service';
import { MaskService } from '@shared/services/mask.service';
import { ConferenciaContagemRequest } from '@shared/services/requests/compras/conferencia-contagem.request';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import { ConferenciaItemStruct } from '@shared/services/structs/compras/conferencia-item.struct';
import { UtilService } from '@shared/services/util.service';
import { ConferenciaDialogData, ConferenciaDialogResult } from './conferencia-dialog.data';

const TAMANHO_MAXIMIZADO = { largura: '100vw', altura: '100vh' };
const TAMANHO_RESTAURADO = { largura: '96vw', altura: '92vh' };
const ESPERA_SALVAR_MS = 600;
const DESTAQUE_MS = 1600;

/**
 * Tela cheia de contagem de uma ação da conferência física: "Receber mercadorias" (volumes) ou
 * "Conferir itens". Cada alteração de contagem é gravada (debounce) para nunca perder a parcial;
 * Concluir apura a divergência no backend. "Registrar problema" é um painel lateral do próprio diálogo.
 * Moldura do Kanban (abre em 96vw × 92vh; o botão do cabeçalho maximiza e volta):
 *
 * ```ts
 * this.dialog.open<ConferenciaDialogComponent, ConferenciaDialogData, ConferenciaDialogResult>(ConferenciaDialogComponent, {
 *   width: '96vw', height: '92vh', maxWidth: '100vw', maxHeight: '100vh',
 *   disableClose: true, autoFocus: false, panelClass: 'conferencia-dialog', data,
 * }).afterClosed().subscribe(resultado => { ... });
 * ```
 */
@Component({
  selector: 'app-conferencia-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ContadorQuantidadeComponent,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './conferencia-dialog.component.html',
  styleUrl: './conferencia-dialog.component.css',
})
export class ConferenciaDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('campoBusca') campoBusca?: ElementRef<HTMLInputElement>;

  readonly statusItem = StatusItemConferenciaEnum;
  readonly tipoDivergencia = TipoDivergenciaCompraEnum;
  readonly isVolumes: boolean;
  /** Volumes são inteiros; itens seguem as casas de quantidade da empresa. */
  readonly quantidadeMask: ReturnType<MaskService['getIntegerMask']> | ReturnType<MaskService['getDynamicDecimalMask']>;
  readonly quantidadeProblemaMask: ReturnType<MaskService['getDynamicDecimalMask']>;

  isMaximizado = false;
  volumesRecebidos: number | null;
  /** Cópia local dos itens: a contagem é editada aqui e enviada inteira a cada gravação. */
  itens: ConferenciaItemStruct[];
  itensVisiveis: ConferenciaItemStruct[];
  termoBusca = '';
  quantidadeBusca: number | null = null;
  idItemDestaque: number | null = null;
  concluindo = false;
  fechando = false;

  problemaAberto = false;
  salvandoProblema = false;
  problemaTipo = TipoDivergenciaCompraEnum.Quantidade;
  problemaIdItem: number | null = null;
  problemaReferencia = '';
  problemaQuantidade: number | null = null;
  problemaDescricao = '';

  private alterado = false;
  private salvando = false;
  private pendente = false;
  /** Envio em andamento (ou o último): resolve `false` quando a gravação falhou. */
  private salvamento: Promise<boolean> = Promise.resolve(true);
  private timerSalvar: ReturnType<typeof setTimeout> | null = null;
  private timerDestaque: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ConferenciaDialogData,
    private dialogRef: MatDialogRef<ConferenciaDialogComponent, ConferenciaDialogResult>,
    private conferenciaService: ConferenciaService,
    private automacaoService: AutomacaoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private elementRef: ElementRef<HTMLElement>,
    maskService: MaskService,
  ) {
    this.isVolumes = data.conferencia.idAcaoEtapa === AcaoEtapaEnum.ConferirVolumes;
    this.quantidadeMask = this.isVolumes ? maskService.getIntegerMask() : maskService.getDynamicDecimalMask(null, false);
    this.quantidadeProblemaMask = maskService.getDynamicDecimalMask(null, false);
    this.volumesRecebidos = data.conferencia.quantidadeVolumesRecebida;
    this.itens = (data.conferencia.itens ?? []).map(item => ({ ...item }));
    this.itensVisiveis = this.itens;
  }

  ngAfterViewInit(): void {
    // Leitor de código de barras digita no campo focado: já abre pronto para bipar.
    if (!this.data.somenteLeitura) setTimeout(() => this.campoBusca?.nativeElement.focus());
  }

  ngOnDestroy(): void {
    if (this.timerSalvar) clearTimeout(this.timerSalvar);
    if (this.timerDestaque) clearTimeout(this.timerDestaque);
  }

  // ------------------------------------------------------------------------------------------
  // Leitura para o template
  // ------------------------------------------------------------------------------------------

  get titulo(): string {
    return this.isVolumes ? 'Recebimento de mercadorias' : 'Conferência de itens';
  }

  get subtitulo(): string {
    return this.isVolumes ? 'Contagem de volumes recebidos' : 'Contagem item a item contra a nota';
  }

  get numeroNota(): string {
    const nota = this.data.nota;
    if (!nota?.numero) return '—';
    return nota.serie ? `${nota.numero}/${nota.serie}` : nota.numero;
  }

  /** Cabeçalho: a NF conferida e os pedidos dela (a conferência cobre todos). */
  get identificacao(): string {
    const codigos = this.data.codigosPedido ?? [];
    const pedidos = codigos.length ? ` · ${codigos.length === 1 ? 'Pedido' : 'Pedidos'} ${codigos.join(', ')}` : '';
    return `NF ${this.numeroNota}${pedidos}`;
  }

  /** Esperado visível: modo aberto, ou ação já concluída (o backend devolve tudo depois de concluir). */
  get exibeEsperado(): boolean {
    return !this.data.isCega || this.data.somenteLeitura;
  }

  get textoVolumes(): string {
    if (!this.exibeEsperado) return 'Modo cego: a quantidade esperada não é exibida durante a contagem.';
    const esperado = this.data.conferencia.quantidadeVolumesEsperada;
    return `Quantidade esperada na nota: ${esperado == null ? 'não informada' : esperado}.`;
  }

  get textoRodape(): string {
    if (this.data.somenteLeitura) return 'Contagem concluída.';
    return this.isVolumes
      ? 'A divergência é apurada automaticamente ao concluir a contagem.'
      : 'Ao concluir, o sistema compara o contado com o esperado e sinaliza divergências.';
  }

  get ocupado(): boolean {
    return this.concluindo || this.fechando;
  }

  get motivoRegistrarProblema(): string {
    return this.data.podeRegistrarProblema ? '' : 'Seu perfil não tem permissão para registrar problemas nesta etapa.';
  }

  get motivoSalvarProblema(): string {
    return this.problemaDescricao.trim() ? '' : 'Descreva o problema para salvar.';
  }

  /**
   * Ponto de status ao vivo. Aberto: verde = conferido igual ao esperado, vermelho = diferente, âmbar = não tocado.
   * Cego: só tocado (cinza) ou não tocado (âmbar).
   */
  classePonto(item: ConferenciaItemStruct): string {
    if (item.quantidadeConferida == null) return 'cfd-ponto--pendente';
    if (!this.exibeEsperado || item.quantidadeEsperada == null) return 'cfd-ponto--tocado';
    return item.quantidadeConferida === item.quantidadeEsperada ? 'cfd-ponto--ok' : 'cfd-ponto--divergente';
  }

  tooltipPonto(item: ConferenciaItemStruct): string {
    if (item.quantidadeConferida == null) return 'Não contado';
    if (!this.exibeEsperado || item.quantidadeEsperada == null) return 'Contado';
    return item.quantidadeConferida === item.quantidadeEsperada ? 'Conferido' : 'Diferente do esperado';
  }

  // ------------------------------------------------------------------------------------------
  // Moldura
  // ------------------------------------------------------------------------------------------

  alternarTamanho(): void {
    this.isMaximizado = !this.isMaximizado;
    const tamanho = this.isMaximizado ? TAMANHO_MAXIMIZADO : TAMANHO_RESTAURADO;
    this.dialogRef.updateSize(tamanho.largura, tamanho.altura);
    this.dialogRef.updatePosition();
    // Tela cheia sem cantos arredondados, como as demais molduras do Kanban.
    if (this.isMaximizado) this.dialogRef.addPanelClass('associacao-dialog-maximizado');
    else this.dialogRef.removePanelClass('associacao-dialog-maximizado');
    // Solta o foco do mat-icon-button para o state layer sumir; teclado continua com :focus-visible.
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  // ------------------------------------------------------------------------------------------
  // Contagem
  // ------------------------------------------------------------------------------------------

  alterarItem(item: ConferenciaItemStruct, delta: number): void {
    if (this.data.somenteLeitura) return;
    const casas = this.quantidadeMask.precision;
    item.quantidadeConferida = Math.max(0, UtilService.arredondarAbnt((item.quantidadeConferida ?? 0) + delta, casas));
    this.agendarSalvar();
  }

  /** Contagem alterada no contador (− / + ou digitada; já arredondada e sem negativo): gravação adiada. */
  definirVolumes(valor: number): void {
    if (this.data.somenteLeitura) return;
    this.volumesRecebidos = valor;
    this.agendarSalvar();
  }

  definirItem(item: ConferenciaItemStruct, valor: number): void {
    if (this.data.somenteLeitura) return;
    item.quantidadeConferida = valor;
    this.agendarSalvar();
  }

  onTermoBuscaChange(termo: string): void {
    this.termoBusca = termo;
    if (!termo.trim()) this.itensVisiveis = this.itens;
  }

  /**
   * Enter na busca (digitado ou bipado; só na ação de itens). SKU ou EAN da nota igual (sem diferenciar
   * maiúsculas) ou nome contendo o texto; um item recebe a quantidade e é destacado, vários filtram a tabela.
   */
  lancarBusca(): void {
    if (this.data.somenteLeitura || this.ocupado) return;
    const quantidade = this.quantidadeBusca && this.quantidadeBusca > 0 ? this.quantidadeBusca : 1;
    const termo = this.termoBusca.trim().toLowerCase();
    if (!termo) return;
    // Código exato: SKU ou EAN do item na nota (o que o leitor bipa da caixa).
    const isCodigo = (i: ConferenciaItemStruct) => (i.codigoSku ?? '').toLowerCase() === termo
      || (i.idNFeEntradaItem != null && this.data.eanPorNFeEntradaItem?.[i.idNFeEntradaItem] === termo);
    const porCodigo = this.itens.filter(isCodigo);
    const encontrados = porCodigo.length === 1
      ? porCodigo
      : this.itens.filter(i => isCodigo(i) || (i.nome ?? '').toLowerCase().includes(termo));

    if (!encontrados.length) {
      this.alertService.show('Conferência', `Nenhum item encontrado para "${this.termoBusca.trim()}".`, AlertType.warning);
      return;
    }
    if (encontrados.length > 1) {
      this.itensVisiveis = encontrados;
      return;
    }

    const item = encontrados[0];
    this.alterarItem(item, quantidade);
    this.limparBusca();
    this.destacar(item.idConferenciaItem);
  }

  private limparBusca(): void {
    this.termoBusca = '';
    this.quantidadeBusca = null;
    this.itensVisiveis = this.itens;
  }

  private destacar(idConferenciaItem: number): void {
    this.idItemDestaque = idConferenciaItem;
    if (this.timerDestaque) clearTimeout(this.timerDestaque);
    this.timerDestaque = setTimeout(() => { this.idItemDestaque = null; this.timerDestaque = null; }, DESTAQUE_MS);
    setTimeout(() => this.elementRef.nativeElement
      .querySelector(`[data-item="${idConferenciaItem}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }

  // ------------------------------------------------------------------------------------------
  // Gravação (debounce; um envio por vez, reenviando se mudou durante o envio)
  // ------------------------------------------------------------------------------------------

  private agendarSalvar(): void {
    this.alterado = true;
    this.pendente = true;
    if (this.timerSalvar) clearTimeout(this.timerSalvar);
    this.timerSalvar = setTimeout(() => { this.timerSalvar = null; this.salvarPendente(); }, ESPERA_SALVAR_MS);
  }

  private salvarPendente(): void {
    if (this.salvando || !this.pendente) return;
    this.salvando = true;
    this.salvamento = this.enviarContagem().finally(() => { this.salvando = false; });
  }

  private async enviarContagem(): Promise<boolean> {
    try {
      while (this.pendente) {
        this.pendente = false;
        const resposta = await firstValueFrom(this.conferenciaService.salvarContagem(this.montarContagem()));
        if (resposta.isError) throw new Error(resposta.errorDescription);
      }
      return true;
    } catch (e) {
      // A contagem continua na tela e volta a ficar pendente: a próxima gravação (ou o concluir) leva tudo.
      this.pendente = true;
      this.exibirErro(e, 'Não foi possível salvar a contagem.');
      return false;
    }
  }

  /** Grava o que estiver pendente (agendado ou de uma falha anterior) e espera o envio terminar; `false` = falhou. */
  private descarregarGravacao(): Promise<boolean> {
    if (this.timerSalvar) {
      clearTimeout(this.timerSalvar);
      this.timerSalvar = null;
    }
    this.salvarPendente();
    return this.salvamento;
  }

  private montarContagem(): ConferenciaContagemRequest {
    return {
      idConferencia: this.data.conferencia.idConferencia,
      quantidadeVolumesRecebida: this.isVolumes ? this.volumesRecebidos : null,
      itens: this.itens.map(i => ({ idConferenciaItem: i.idConferenciaItem, quantidadeConferida: i.quantidadeConferida })),
    };
  }

  // ------------------------------------------------------------------------------------------
  // Rodapé
  // ------------------------------------------------------------------------------------------

  async concluir(): Promise<void> {
    if (this.data.somenteLeitura || this.ocupado) return;
    if (!await this.confirmarConclusao()) return;
    this.concluindo = true;
    try {
      // O concluir leva a contagem inteira; basta esperar o envio em andamento para não cruzar gravações.
      if (this.timerSalvar) { clearTimeout(this.timerSalvar); this.timerSalvar = null; }
      this.pendente = false;
      await this.salvamento;
      // Concluir notifica "Ao concluir a conferência": perfil -> usuários vive na API de Usuário, então expande antes.
      const idsUsuarioPerfil = await firstValueFrom(
        this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.ConclusaoConferencia));
      const resposta = await firstValueFrom(this.conferenciaService.concluir({ ...this.montarContagem(), idsUsuarioPerfil }));
      if (resposta.isError) throw new Error(resposta.errorDescription);
      this.alertService.show('Sucesso',
        this.isVolumes ? 'Recebimento de mercadorias concluído.' : 'Conferência de itens concluída.', AlertType.success);
      this.dialogRef.close({ alterado: true, concluido: true });
    } catch (e) {
      // A contagem não foi gravada pelo concluir: volta a ficar pendente para a próxima gravação ou o Cancelar.
      this.pendente = true;
      this.exibirErro(e, 'Não foi possível concluir a contagem.');
    } finally {
      this.concluindo = false;
    }
  }

  /**
   * Concluir é irreversível (só "Reabrir contagem", com permissão) e o que não foi contado vira 0 na apuração:
   * confirma antes, informando o que está sem contagem.
   */
  private async confirmarConclusao(): Promise<boolean> {
    let aviso: string;
    if (this.isVolumes) {
      aviso = this.volumesRecebidos == null
        ? ' Os <b>volumes recebidos não foram informados</b> e serão considerados 0.'
        : '';
    } else {
      const semContagem = this.itens.filter(i => i.quantidadeConferida == null).length;
      aviso = semContagem
        ? ` <b>${semContagem} ${semContagem === 1 ? 'item está' : 'itens estão'} sem contagem</b> e `
          + `${semContagem === 1 ? 'será considerado' : 'serão considerados'} 0, ficando como divergentes.`
        : '';
    }
    const nomeAcao = this.isVolumes ? 'o recebimento de mercadorias' : 'a conferência de itens';
    const resultado = await firstValueFrom(this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Concluir contagem',
        innerHtml: `Ao concluir ${nomeAcao}, a contagem não pode mais ser alterada e as divergências são apuradas.${aviso}`,
        buttonConfirm: 'Concluir contagem',
        buttonClose: 'Voltar',
      },
    }).afterClosed());
    return !!resultado?.confirm;
  }

  /** Cancelar e X: a contagem parcial fica gravada; volta ao card. Se a gravação falhar, só fecha confirmando. */
  async cancelar(): Promise<void> {
    if (this.ocupado) return;
    this.fechando = true;
    let gravou = false;
    try {
      gravou = await this.descarregarGravacao();
    } finally {
      this.fechando = false;
    }
    if (!gravou && !await this.confirmarFecharSemGravar()) return;
    this.dialogRef.close({ alterado: this.alterado, concluido: false });
  }

  private async confirmarFecharSemGravar(): Promise<boolean> {
    const resultado = await firstValueFrom(this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Contagem não gravada',
        innerHtml: 'Não foi possível gravar as últimas alterações da contagem. Se fechar agora, elas serão '
          + '<b>perdidas</b>. Continue na contagem para tentar de novo ao fechar ou concluir.',
        buttonConfirm: 'Fechar sem gravar',
        buttonClose: 'Continuar contagem',
      },
    }).afterClosed());
    return !!resultado?.confirm;
  }

  // ------------------------------------------------------------------------------------------
  // Painel "Registrar problema"
  // ------------------------------------------------------------------------------------------

  abrirProblema(): void {
    if (this.data.somenteLeitura || !this.data.podeRegistrarProblema) return;
    this.problemaTipo = TipoDivergenciaCompraEnum.Quantidade;
    this.problemaIdItem = null;
    this.problemaReferencia = '';
    this.problemaQuantidade = null;
    this.problemaDescricao = '';
    this.problemaAberto = true;
  }

  fecharProblema(): void {
    if (this.salvandoProblema) return;
    this.problemaAberto = false;
  }

  async salvarProblema(): Promise<void> {
    if (this.salvandoProblema || this.motivoSalvarProblema) return;
    this.salvandoProblema = true;
    try {
      // O problema notifica "Divergência registrada": expande os perfis como no concluir.
      const idsUsuarioPerfil = await firstValueFrom(this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.DivergenciaRegistrada));
      const resposta = await firstValueFrom(this.conferenciaService.registrarProblema({
        idsUsuarioPerfil,
        idConferencia: this.data.conferencia.idConferencia,
        idTipoDivergenciaCompra: this.problemaTipo,
        idConferenciaItem: this.problemaIdItem,
        referencia: this.problemaIdItem ? null : this.problemaReferencia.trim() || null,
        quantidade: this.problemaQuantidade,
        descricao: this.problemaDescricao.trim(),
      }));
      if (resposta.isError) throw new Error(resposta.errorDescription);
      this.alterado = true;
      this.problemaAberto = false;
      this.alertService.show('Sucesso', 'Problema registrado. A decisão de mover o card continua manual.', AlertType.success);
    } catch (e) {
      this.exibirErro(e, 'Não foi possível registrar o problema.');
    } finally {
      this.salvandoProblema = false;
    }
  }

  private exibirErro(e: unknown, padrao: string): void {
    this.alertService.show('Conferência', e instanceof Error && e.message ? e.message : padrao, AlertType.error);
  }
}
